<script setup>
// Config do nó Início — gatilho (palavra-chave) + horário de atendimento (business hours).
// Espelha StartTriggerConfig. O keyword é gravado no trigger do flow (usado pelo listener nativo).
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });

const { t } = useI18n();

const aux = inject('flowAux', {});
const d = computed(() => props.node.data);
const isSequence = computed(() => aux.flowType === 'sequence');

const DAYS = [
  ['CHATBOT.nodeConfig.startTrigger.days.sun', 0],
  ['CHATBOT.nodeConfig.startTrigger.days.mon', 1],
  ['CHATBOT.nodeConfig.startTrigger.days.tue', 2],
  ['CHATBOT.nodeConfig.startTrigger.days.wed', 3],
  ['CHATBOT.nodeConfig.startTrigger.days.thu', 4],
  ['CHATBOT.nodeConfig.startTrigger.days.fri', 5],
  ['CHATBOT.nodeConfig.startTrigger.days.sat', 6],
];
const TZ = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Cuiaba',
  'America/Belem',
  'America/Rio_Branco',
  'America/Noronha',
  'UTC',
].map(tz => ({ value: tz, label: tz }));

const ensureBusinessHours = () => {
  if (!d.value.businessHours) d.value.businessHours = {};
  return d.value.businessHours;
};
const bh = computed(() => d.value.businessHours || {});
const toggleDay = day => {
  const target = ensureBusinessHours();
  const days = target.days || [];
  target.days = days.includes(day)
    ? days.filter(x => x !== day)
    : [...days, day];
};

onMounted(() => {
  if (!d.value.businessHours) {
    d.value.businessHours = {
      enabled: false,
      timezone: 'America/Sao_Paulo',
      days: [1, 2, 3, 4, 5],
      startTime: '08:00',
      endTime: '18:00',
      lunchEnabled: false,
      lunchStart: '12:00',
      lunchEnd: '13:00',
      outsideAction: 'ignore',
      outsideMessage: '',
    };
  }
});
</script>

<template>
  <div>
    <template v-if="isSequence">
      <div
        class="p-2 mb-3 text-[0.6875rem] rounded bg-n-alpha-1 border border-n-weak text-n-slate-11"
      >
        {{ t('CHATBOT.nodeConfig.startTrigger.sequenceDescription') }}
      </div>
      <FlowField
        :model-value="!!d.stopOnUserReply"
        :label="t('CHATBOT.nodeConfig.startTrigger.stopOnReplyLabel')"
        type="checkbox"
        @update:model-value="d.stopOnUserReply = $event"
      />
      <p class="text-xs text-n-slate-10 mt-1">
        {{ t('CHATBOT.nodeConfig.startTrigger.stopOnReplyHint') }}
      </p>
    </template>

    <template v-else>
      <FlowField
        :model-value="d.startRule"
        :label="t('CHATBOT.nodeConfig.startTrigger.startRuleLabel')"
        type="text"
        :placeholder="t('CHATBOT.nodeConfig.startTrigger.startRulePlaceholder')"
        :hint="t('CHATBOT.nodeConfig.startTrigger.startRuleHint')"
        @update:model-value="d.startRule = $event"
      />

      <div class="pt-2 mt-2 border-t border-n-weak">
        <FlowField
          :model-value="!!bh.enabled"
          :label="t('CHATBOT.nodeConfig.startTrigger.businessHoursTitle')"
          type="checkbox"
          @update:model-value="bh.enabled = $event"
        />

        <template v-if="bh.enabled">
          <FlowField
            :model-value="bh.timezone"
            :label="t('CHATBOT.nodeConfig.startTrigger.timezone')"
            type="select"
            :options="TZ"
            @update:model-value="bh.timezone = $event"
          />

          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('CHATBOT.nodeConfig.startTrigger.workDays')
          }}</label>
          <div class="flex gap-1 mb-3">
            <button
              v-for="[lbl, day] in DAYS"
              :key="day"
              class="flex-1 px-2 py-1.5 text-xs rounded-md border border-n-weak text-n-slate-11 bg-n-background"
              :class="{
                'bg-n-brand text-white border-n-brand': (
                  bh.days || []
                ).includes(day),
              }"
              @click="toggleDay(day)"
            >
              {{ t(lbl) }}
            </button>
          </div>

          <div class="flex gap-2">
            <FlowField
              class="flex-1"
              :model-value="bh.startTime"
              :label="t('CHATBOT.nodeConfig.startTrigger.startTime')"
              type="time"
              @update:model-value="bh.startTime = $event"
            />
            <FlowField
              class="flex-1"
              :model-value="bh.endTime"
              :label="t('CHATBOT.nodeConfig.startTrigger.endTime')"
              type="time"
              @update:model-value="bh.endTime = $event"
            />
          </div>

          <FlowField
            :model-value="!!bh.lunchEnabled"
            :label="t('CHATBOT.nodeConfig.startTrigger.lunchBreak')"
            type="checkbox"
            @update:model-value="bh.lunchEnabled = $event"
          />
          <div v-if="bh.lunchEnabled" class="flex gap-2">
            <FlowField
              class="flex-1"
              :model-value="bh.lunchStart"
              :label="t('CHATBOT.nodeConfig.startTrigger.lunchStart')"
              type="time"
              @update:model-value="bh.lunchStart = $event"
            />
            <FlowField
              class="flex-1"
              :model-value="bh.lunchEnd"
              :label="t('CHATBOT.nodeConfig.startTrigger.lunchEnd')"
              type="time"
              @update:model-value="bh.lunchEnd = $event"
            />
          </div>

          <FlowField
            :model-value="bh.outsideAction"
            :label="t('CHATBOT.nodeConfig.startTrigger.outsideHours')"
            type="select"
            :options="[
              {
                value: 'ignore',
                label: t('CHATBOT.nodeConfig.startTrigger.outsideIgnore'),
              },
              {
                value: 'message',
                label: t('CHATBOT.nodeConfig.startTrigger.outsideMessage'),
              },
            ]"
            @update:model-value="bh.outsideAction = $event"
          />
          <FlowField
            v-if="bh.outsideAction === 'message'"
            :model-value="bh.outsideMessage"
            :label="t('CHATBOT.nodeConfig.startTrigger.outsideMessage')"
            type="textarea"
            :rows="3"
            :placeholder="
              t('CHATBOT.nodeConfig.startTrigger.outsideMessagePlaceholder')
            "
            @update:model-value="bh.outsideMessage = $event"
          />
        </template>
      </div>
    </template>
  </div>
</template>
